import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Spinner from "../ogComponent/spinner";
import axios from "axios";
import { IconPencilMinus, IconTrash } from "@tabler/icons-react";
import { Box, Modal } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditLeaveModal from "./cl_edit_modal";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import "./leaveShow.css";

const LeaveShow = () => {
  const [product, setProduct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);

  const permissions = useSelector((state) => state.emp.permissions);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/leavemanagement/company-holiday/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data) {
        // Sort by start_date ascending
        const sortedData = [...response.data].sort((a, b) =>
          new Date(a.start_date) - new Date(b.start_date)
        );
        setProduct(sortedData);
      }
    } catch (error) {
      console.error("Error fetching company leaves:", error);
      toast.error("Failed to fetch company leaves");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.includes("showcompanyleaveID")) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [permissions]);

  const handleEditClick = (leaveId) => {
    if (permissions.includes("editcompanyleaveID")) {
      setSelectedLeaveId(leaveId);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLeaveId(null);
  };

  const handleOpenModal = (e, item) => {
    if (permissions.includes("deletecompanyleaveID")) {
      e.stopPropagation();
      setSelectedRow(item);
      setDeleteModalOpen(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRow(null);
  };

  const handleDelete = async () => {
    if (!selectedRow || !permissions.includes("deletecompanyleaveID")) return;

    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(
        `${process.env.REACT_APP_API}/api/leavemanagement/company-holiday/${selectedRow.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setProduct((prev) => prev.filter((item) => item.id !== selectedRow.id));
      handleCloseDeleteModal();
      toast.success("Leave deleted successfully!");
    } catch (error) {
      console.error("Error deleting leave:", error);
      toast.error("Failed to delete leave");
    }
  };

  const handleLeaveUpdated = () => {
    fetchData(); // Simplified: just refetch data on update
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: 24,
    minWidth: 320,
    maxWidth: 400,
    width: "90%",
  };

  const isGroupWarm = (title, startDate) => {
    const titleLower = title.toLowerCase();
    return (
      titleLower.includes("pooja holiday - 1") ||
      titleLower.includes("eid-ul-adha") ||
      (titleLower.includes("pooja holiday - 1") && startDate === "2026-08-20")
    );
  };

  const isGroupBlue = (title, startDate) => {
    const titleLower = title.toLowerCase();
    return (
      titleLower.includes("miladunnabi") ||
      (titleLower.includes("onam") && startDate === "2026-08-25")
    );
  };

  if (loading) return <Spinner />;

  return (
    <div className="leaveShowContainer w-100 d-flex flex-column p-0 gap-4">
      <div className="d-flex justify-content-between align-items-center mb-2 px-2">
        <h3 className="m-0">Company Holiday Calendar</h3>
        {permissions.includes("addcompanyleaveID") && (
          <Link to="/user/functions?tabId=4" className="new-leave-btn text-decoration-none">
            <i className="bi bi-calendar-plus me-1"></i> New Holiday
          </Link>
        )}
      </div>

      <div className="legend-container mx-2 shadow-sm">
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#f59e0b' }}></div>
          <span>Group A: Optional (Choose one)</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#3b82f6' }}></div>
          <span>Group B: Optional (Choose one)</span>
        </div>
        <div className="legend-item ms-auto d-none d-md-flex">
          <span className="text-muted fw-bold">{product.length} Holidays Planned</span>
        </div>
      </div>

      <div className="px-2 mt-2">
        {permissions.includes("showcompanyleaveID") ? (
          product.length === 0 ? (
            <div className="d-flex flex-column justify-content-center align-items-center py-5 shadow-sm rounded-4 bg-white">
              <img src="/assets/file.png" alt="empty" style={{ width: 120, opacity: 0.2 }} />
              <p className="mt-3 fw-medium text-muted">No holidays scheduled yet</p>
            </div>
          ) : (
            <div className="holiday-grid">
              {product.map((item) => {
                const groupWarm = isGroupWarm(item.title, item.start_date);
                const groupBlue = isGroupBlue(item.title, item.start_date);
                const startDate = dayjs(item.start_date);

                return (
                  <div
                    key={item.id}
                    className={`holiday-card ${groupWarm ? 'group-warm' : groupBlue ? 'group-blue' : ''} ${dayjs(item.start_date).isBefore(dayjs(), 'day') ? 'past' : ''
                      }`}
                  >
                    <div className="d-flex justify-content-between align-items-start h-100">
                      <div className="d-flex flex-column h-100 w-100">
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div className="holiday-date-badge shadow-sm">
                            <span className="holiday-day">{startDate.format("DD")}</span>
                            <span className="holiday-month-year">{startDate.format("MMM YYYY")}</span>
                          </div>
                          {item.image && (
                            <img src={item.image} alt="" className="holiday-image ms-auto" />
                          )}
                        </div>

                        <h4 className="holiday-title">{item.title}</h4>

                        <div className="holiday-status">
                          <i className="bi bi-clock"></i>
                          {item.days ? `${item.days} Day(s)` : 'Full Day'}
                        </div>

                        {groupWarm && (
                          <span className="holiday-group-tag group-warm-tag">Optional Group A</span>
                        )}
                        {groupBlue && (
                          <span className="holiday-group-tag group-blue-tag">Optional Group B</span>
                        )}
                      </div>

                      <div className="holiday-actions">
                        {permissions.includes("editcompanyleaveID") && (
                          <button
                            className="holiday-action-btn edit"
                            onClick={(e) => handleEditClick(item.id)}
                            title="Edit Holiday"
                          >
                            <IconPencilMinus size={18} />
                          </button>
                        )}
                        {permissions.includes("deletecompanyleaveID") && (
                          <button
                            className="holiday-action-btn delete"
                            onClick={(e) => handleOpenModal(e, item)}
                            title="Delete Holiday"
                          >
                            <IconTrash size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="alert alert-warning">No permission to view company leaves.</div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") handleCloseDeleteModal();
        }}
        disableEscapeKeyDown
      >
        <Box sx={modalStyle} className="modalmainbox p-4 border-0">
          <div className="w-100 d-flex align-items-center flex-column">
            <WarningAmberIcon sx={{ color: "#ef4444", fontSize: 64 }} />
            <h4 className="fw-bold mt-3 mb-2">Delete Holiday?</h4>
            <p className="text-muted text-center mb-4">
              This action cannot be undone. Are you sure you want to remove <strong className="text-dark">{selectedRow?.title}</strong>?
            </p>
          </div>
          <div className="d-flex justify-content-center gap-3 w-100">
            <button className="btn btn-light px-4 py-2 rounded-3 fw-bold" onClick={handleCloseDeleteModal}>
              Cancel
            </button>
            <button className="btn btn-danger px-4 py-2 rounded-3 fw-bold shadow-sm" onClick={handleDelete}>
              Delete Now
            </button>
          </div>
        </Box>
      </Modal>

      {isModalOpen && permissions.includes("editcompanyleaveID") && (
        <EditLeaveModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          leaveId={selectedLeaveId}
          onLeaveUpdated={handleLeaveUpdated}
        />
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default LeaveShow;
